import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PROBLEM_CODES, api, errorMessage, problemCode, useApiMutation } from "../../api";
import { Button, EmptyState, useToast } from "../../components";
import { ROUTES } from "../../shell";
import styles from "./Auth.module.css";
import { ChampMotDePasse } from "./ChampMotDePasse";
import { erreurConfirmation, erreurMotDePasse, jetonDuFragment } from "./MotDePasse";

/** Pose un nouveau mot de passe depuis le lien reçu par courriel. */
export function ReinitialisationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { notify } = useToast();
  const token = jetonDuFragment(location.hash);
  const [mdp, setMdp] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [touche, setTouche] = useState(false);
  const reset = useApiMutation((t: string, p: string) => api.resetPassword({ token: t, password: p }));

  const erreurMdp = touche ? erreurMotDePasse(mdp) : null;
  const erreurConf = touche ? erreurConfirmation(mdp, confirmation) : null;

  if (token === null) {
    return (
      <EmptyState
        icon="🔗"
        title="Lien incomplet"
        description="Ce lien ne porte pas de jeton. Recopiez l'adresse complète du courriel, ou demandez un nouveau lien."
        action={<Link to={ROUTES.motDePasseOublie}>Demander un nouveau lien</Link>}
      />
    );
  }

  if (problemCode(reset.error) === PROBLEM_CODES.passwordResetInvalid) {
    return (
      <EmptyState
        icon="⌛"
        title="Ce lien ne vaut plus"
        description="Il a expiré, a déjà servi, ou n'a jamais existé. Demandez-en un nouveau : le précédent est de toute façon mort."
        action={<Link to={ROUTES.motDePasseOublie}>Demander un nouveau lien</Link>}
      />
    );
  }

  async function soumettre(e: FormEvent) {
    e.preventDefault();
    setTouche(true);
    if (erreurMotDePasse(mdp) !== null || erreurConfirmation(mdp, confirmation) !== null || token === null) {
      return;
    }
    const ok = await reset.run(token, mdp);
    if (ok === null) {
      return;
    }
    notify({ title: "Mot de passe modifié", description: "Connectez-vous avec le nouveau.", tone: "success" });
    navigate(ROUTES.connexion, { replace: true });
  }

  return (
    <>
      <div>
        <h1 className={styles.titre}>Nouveau mot de passe</h1>
        <p className={styles.intro}>Toutes vos sessions ouvertes seront fermées.</p>
      </div>
      <form className={styles.formulaire} onSubmit={(e) => void soumettre(e)} noValidate>
        {reset.error !== null && (
          <p className={styles.alerte} role="alert">
            {errorMessage(reset.error)}
          </p>
        )}
        <ChampMotDePasse
          label="Nouveau mot de passe"
          value={mdp}
          onChange={setMdp}
          autoComplete="new-password"
          showRules
          error={erreurMdp}
          disabled={reset.pending}
        />
        <ChampMotDePasse
          label="Confirmation"
          value={confirmation}
          onChange={setConfirmation}
          autoComplete="new-password"
          error={erreurConf}
          disabled={reset.pending}
        />
        <Button type="submit" variant="primary" fullWidth loading={reset.pending}>
          Enregistrer le mot de passe
        </Button>
      </form>
    </>
  );
}
